import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Popconfirm, message, Typography, Modal, Form, Input, Image as AntImage, Space, Upload } from 'antd';
import { PlusOutlined, DeleteOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons';
import { 
    useGetGalleryMutation, 
    useCreateGalleryMutation, 
    useDeleteGalleryMutation, 
    useUploadImagesMutation, 
    useDeleteImageMutation 
} from '../redux/services/apiSlice';

const { Title } = Typography;

const Gallery = () => {
    const [getGallery, { data, isLoading }] = useGetGalleryMutation();
    const [createGallery] = useCreateGalleryMutation();
    const [deleteGallery] = useDeleteGalleryMutation();
    const [uploadImages] = useUploadImagesMutation();
    const [deleteImage] = useDeleteImageMutation();

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState([]);

    // Table state
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [searchText, setSearchText] = useState('');
    const [sortOrder, setSortOrder] = useState('-createdAt');

    const fetchGallery = useCallback((page = 1, limit = 10, search = '', sort = '-createdAt') => {
        getGallery({ page, limit, search, sort });
    }, [getGallery]);

    useEffect(() => {
        fetchGallery(pagination.current, pagination.pageSize, searchText, sortOrder);
    }, [fetchGallery, pagination.current, pagination.pageSize, searchText, sortOrder]);

    const handleTableChange = (newPagination, filters, sorter) => {
        setPagination(newPagination);
        if (sorter.field) {
            const order = sorter.order === 'ascend' ? sorter.field : `-${sorter.field}`;
            setSortOrder(order);
        }
    };

    const handleSearch = (value) => {
        setSearchText(value);
        setPagination({ ...pagination, current: 1 });
    };

    const handleCustomRequest = async ({ file, onSuccess, onError }) => {
        const formData = new FormData();
        formData.append('images', file);
        try {
            const res = await uploadImages(formData).unwrap();
            onSuccess(res);
            message.success(`${file.name} uploaded successfully`);
        } catch (err) {
            onError(err);
            message.error(`${file.name} upload failed`);
        }
    };

    const handleRemoveFile = async (file) => {
        try {
            const filename = file.response?.files[0]?.name || file.name;
            await deleteImage(filename).unwrap();
            message.success('Image removed from server');
            return true;
        } catch (error) {
            message.error('Failed to remove image from server');
            return false;
        }
    };

    const handleCreate = async (values) => {
        try {
            if (fileList.length === 0 || fileList[0].status !== 'done') {
                return message.error('Please upload an image');
            }

            const uploadedFile = fileList[0].response.files[0];
            const finalValues = {
                ...values,
                image: {
                    url: uploadedFile.url,
                    name: uploadedFile.name,
                    isPrimary: true
                }
            };

            await createGallery(finalValues).unwrap();
            message.success('Gallery item added');
            setIsModalVisible(false);
            form.resetFields();
            setFileList([]);
            fetchGallery(pagination.current, pagination.pageSize, searchText, sortOrder);
        } catch (error) {
            message.error(error.data?.message || 'Error adding item');
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteGallery(id).unwrap();
            message.success('Item deleted');
            fetchGallery(pagination.current, pagination.pageSize, searchText, sortOrder);
        } catch (error) {
            message.error('Error deleting');
        }
    };

    const columns = [
        {
            title: 'Image',
            dataIndex: 'image',
            key: 'image',
            render: (img) => <AntImage src={img?.url} width={50} height={50} style={{ objectFit: 'cover', borderRadius: 4 }} />
        },
        { title: 'Customer', dataIndex: 'customerName', key: 'customerName', sorter: true },
        { title: 'Package', dataIndex: 'tourPackageName', key: 'tourPackageName', sorter: true },
        { title: 'Country', dataIndex: 'customerCountry', key: 'customerCountry', sorter: true },
        { title: 'Duration', dataIndex: 'tourDuration', key: 'tourDuration' },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Popconfirm title="Are you sure?" onConfirm={() => handleDelete(record._id)}>
                    <Button type="primary" danger icon={<DeleteOutlined />} size="small" />
                </Popconfirm>
            ),
        }
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, alignItems: 'center' }}>
                <Title level={2} style={{ margin: 0 }}>Gallery Management</Title>
                <Space>
                    <Input.Search
                        placeholder="Search gallery..."
                        onSearch={handleSearch}
                        style={{ width: 300 }}
                        enterButton={<SearchOutlined />}
                    />
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => { setIsModalVisible(true); setFileList([]); form.resetFields(); }}>Add New</Button>
                </Space>
            </div>
            
            <Table 
                dataSource={data?.galleryItems || []} 
                columns={columns} 
                rowKey="_id" 
                loading={isLoading}
                pagination={{
                    current: data?.currentPage || 1,
                    pageSize: pagination.pageSize,
                    total: data?.totalItems || 0,
                    showSizeChanger: true
                }}
                onChange={handleTableChange}
            />

            <Modal title="Add New Gallery Item" open={isModalVisible} onCancel={() => setIsModalVisible(false)} onOk={() => form.submit()}>
                <Form form={form} onFinish={handleCreate} layout="vertical">
                    <Form.Item label="Customer Photo" required>
                        <Upload
                            listType="picture-card"
                            fileList={fileList}
                            customRequest={handleCustomRequest}
                            onRemove={handleRemoveFile}
                            onChange={({ fileList }) => setFileList(fileList)}
                            maxCount={1}
                        >
                            {fileList.length >= 1 ? null : (
                                <div>
                                    <PlusOutlined />
                                    <div style={{ marginTop: 8 }}>Upload</div>
                                </div>
                            )}
                        </Upload>
                    </Form.Item>
                    <Form.Item name="customerName" label="Customer Name" rules={[{ required: true }]}><Input /></Form.Item>
                    <Form.Item name="tourPackageName" label="Tour Package Name" rules={[{ required: true }]}><Input /></Form.Item>
                    <Form.Item name="customerCountry" label="Customer Country" rules={[{ required: true }]}><Input /></Form.Item>
                    <Form.Item name="tourDuration" label="Tour Duration" rules={[{ required: true }]}><Input placeholder="e.g. 5D 4N" /></Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Gallery;
